import { useEffect, useState } from "react";
import { ApiError, fetchAutomationEvidence } from "../api";
import { completeMission, loadCompleted } from "../game";
import type { AutomationEvidence } from "../types";
import { AutomationScale } from "./AutomationScale";
import { InterventionImpact } from "./InterventionImpact";
import { OperationalImpact } from "./OperationalImpact";
import { ThreeSystems } from "./ThreeSystems";

/**
 * Automation Evidence.
 *
 * Every figure is read from `/v1/evidence/automation`, which is an allow-listed projection of
 * `ml/artifacts/evaluation.json`. Nothing is computed here and nothing is hardcoded: when the
 * artifact is missing the panel says "Not measured yet" rather than showing a plausible
 * number.
 */

function pct(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

function ratio(value: number): string {
  return value.toFixed(4);
}

export function AutomationEvidencePanel() {
  const [evidence, setEvidence] = useState<AutomationEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The mission-4 stamp: an explicit acknowledgement, remembered across visits.
  const [stamped, setStamped] = useState(() => loadCompleted().has("try-numbers"));

  useEffect(() => {
    fetchAutomationEvidence()
      .then(setEvidence)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 404
            ? "Not measured yet — 평가 산출물(ml/artifacts/evaluation.json)이 없습니다."
            : "Not measured yet — 평가 결과를 불러오지 못했습니다.",
        );
      });
  }, []);

  if (error) {
    return (
      <section className="card">
        <h3>Automation Evidence</h3>
        <p className="muted">{error}</p>
      </section>
    );
  }
  if (!evidence) {
    return (
      <section className="card">
        <h3>Automation Evidence</h3>
        <p className="muted">불러오는 중…</p>
      </section>
    );
  }

  const { rule, ml } = evidence;

  return (
    <>
      {/* Scale and framing first: the numbers below only mean something once you know how
          many transactions there are and what the alternatives cost. */}
      <AutomationScale evidence={evidence} />
      <InterventionImpact />
      <OperationalImpact evidence={evidence} />
      <ThreeSystems evidence={evidence} />

      <section className="card">
        <h3>🤖 &quot;그냥 AI 쓰면 되잖아요?&quot; — 써봤습니다. 성적표가 있어요</h3>
        <p className="callout">
          <strong>한 줄 결론: AI가 더 똑똑했지만, 정작 필요한 과목에서 낙제라서 안
          썼습니다.</strong> 그리고 이 불합격 덕분에 방향을 바꿔서 — 위의 📱 자동
          본인확인이 나왔고, 거기서 사람 일 82% → 55%가 나왔어요. 실패가 낭비가 아니라
          다음 아이디어의 지도였던 거죠.
        </p>
        <p>과목별로 보면:</p>
        <ul className="plain">
          <li>
            🥇 <strong>&quot;사기 잡기&quot; 과목: AI 승.</strong> 잡은 비율{" "}
            {(rule.fraud_recall * 100).toFixed(1)}% → {(ml.fraud_recall * 100).toFixed(1)}%,
            놓친 사기 {rule.false_negatives}건 → {ml.false_negatives}건. 확실히 더 똑똑해요.
          </li>
          <li>
            📉 <strong>&quot;사람 일 줄이기&quot; 과목: AI 낙제.</strong> 사람 없이 자동
            처리되는 비율이 {(rule.approve_rate * 100).toFixed(1)}% →{" "}
            {(ml.approve_rate * 100).toFixed(1)}%로 <strong>오히려 줄었어요.</strong> 이
            프로젝트의 목표가 바로 이 과목인데요.
          </li>
          <li>
            ⚖️ <strong>판정: 불합격 → 투입 안 함.</strong> 합격 기준은 시험 보기{" "}
            <strong>전에</strong> 적어뒀고(아래), 시험 끝나고 기준을 바꾸면 시험이 의미
            없어지니 그대로 따랐습니다.
          </li>
        </ul>
        <p className="muted">
          &quot;그럼 이 패널은 왜 있어요?&quot; — 잘한 것만 보여주는 데모는 믿을 수
          없으니까요. <strong>불합격 성적표를 그대로 공개하는 것</strong>이 이 사이트의
          다른 숫자들을 믿을 수 있게 만드는 장치입니다.
        </p>

      <div className={evidence.application_gate_passed ? "gate-pass" : "gate-fail"}>
        <strong>
          시험 전에 적어둔 합격 기준:{" "}
          {evidence.application_gate_passed ? "통과" : "미통과 (불합격)"}
        </strong>
        <ul>
          <li>
            사기를 충분히 잡는가 (검증셋 기준){" "}
            {evidence.application_gate_results.validation_recall_meets_target
              ? "PASS ✅"
              : "FAIL ❌"}
          </li>
          <li>
            기존 방식보다 사기를 더 놓치진 않는가{" "}
            {evidence.application_gate_results.test_recall_not_materially_worse_than_rule
              ? "PASS ✅"
              : "FAIL ❌"}
          </li>
          <li>
            자동 처리(사람 일 줄이기)가 충분히 늘었는가{" "}
            {evidence.application_gate_results.approve_rate_improvement_meets_minimum
              ? "PASS ✅"
              : "FAIL ❌"}
          </li>
        </ul>
        {!evidence.application_gate_passed && (
          <p>
            그래서 지금 서빙 중인 기본은 <code>{evidence.active_predictor_backend_default}</code>
            (규칙 방식)입니다. 모델 자랑보다, 미리 합의한 기준이 우선.
          </p>
        )}
      </div>

      <details className="raw-json">
        <summary>📊 전체 성적표 펼치기 — 모든 항목에 쉬운 설명을 달아뒀어요</summary>
      <p className="muted">
        고정된 합성 테스트셋 {evidence.test_n.toLocaleString()}건(그중 사기{" "}
        {evidence.test_n_fraud.toLocaleString()}건)으로 딱 한 번 치른 시험입니다. 합성
        데이터라 실제 은행 성능이 아니에요 (dataset <code>{evidence.dataset_id}</code>,
        sha256 <code>{evidence.csv_sha256.slice(0, 12)}…</code>).
      </p>

      <table className="evidence-table">
        <thead>
          <tr>
            <th>항목 (쉬운 설명)</th>
            <th>규칙 방식</th>
            <th>AI 모델</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              판정 기준선
              <div className="muted">이 점수 이상이면 사람에게 (둘은 점수 눈금이 달라요)</div>
            </td>
            <td>{ratio(rule.selected_threshold)}</td>
            <td>{ratio(ml.selected_threshold)}</td>
          </tr>
          <tr>
            <td>
              사기를 잡아낸 비율
              <div className="muted">Fraud Recall — 100%면 사기를 하나도 안 놓침</div>
            </td>
            <td>{pct(rule.fraud_recall)}</td>
            <td>{pct(ml.fraud_recall)}</td>
          </tr>
          <tr>
            <td>
              위 숫자의 오차 범위
              <div className="muted">95% 신뢰구간 — 운이 좋았을 가능성까지 표시</div>
            </td>
            <td>
              [{ratio(rule.fraud_recall_ci95[0])}, {ratio(rule.fraud_recall_ci95[1])}]
            </td>
            <td>
              [{ratio(ml.fraud_recall_ci95[0])}, {ratio(ml.fraud_recall_ci95[1])}]
            </td>
          </tr>
          <tr>
            <td>
              자동 통과율
              <div className="muted">STP rate — 사람 손 없이 끝난 비율. 높을수록 좋음</div>
            </td>
            <td>{pct(rule.approve_rate)}</td>
            <td>{pct(ml.approve_rate)}</td>
          </tr>
          <tr>
            <td>
              사람이 봐야 하는 비율
              <div className="muted">REVIEW율 — 위와 반대. 낮을수록 좋음</div>
            </td>
            <td>{pct(rule.review_rate)}</td>
            <td>{pct(ml.review_rate)}</td>
          </tr>
          <tr>
            <td>
              놓친 사기
              <div className="muted">자동 통과돼 버린 사기 건수. 적을수록 좋음</div>
            </td>
            <td>{rule.false_negatives}건</td>
            <td>{ml.false_negatives}건</td>
          </tr>
          <tr>
            <td>
              위험한 순서로 줄 세우는 능력 ①
              <div className="muted">Average Precision — 높을수록 진짜 사기를 앞줄에 세움</div>
            </td>
            <td>{ratio(rule.average_precision)}</td>
            <td>{ratio(ml.average_precision)}</td>
          </tr>
          <tr>
            <td>
              위험한 순서로 줄 세우는 능력 ②
              <div className="muted">ROC-AUC — 0.5는 동전 던지기, 1.0은 완벽</div>
            </td>
            <td>{ratio(rule.roc_auc)}</td>
            <td>{ratio(ml.roc_auc)}</td>
          </tr>
          <tr>
            <td>
              사람이 10%만 볼 수 있다면 잡는 사기
              <div className="muted">인력이 빠듯할 때 누가 더 유용한가</div>
            </td>
            <td>{pct(rule.recall_at_10pct_review)}</td>
            <td>{pct(ml.recall_at_10pct_review)}</td>
          </tr>
          <tr>
            <td>
              하루에 사람이 봐야 하는 건수
              <div className="muted">하루 {evidence.assumed_daily_transactions.toLocaleString()}건 가정 시 환산</div>
            </td>
            <td>{rule.projected_daily_reviews.toLocaleString()}건</td>
            <td>{ml.projected_daily_reviews.toLocaleString()}건</td>
          </tr>
        </tbody>
      </table>

      <p>
        상대적 REVIEW 감소율: <strong>{pct(evidence.relative_review_reduction)}</strong>{" "}
        <code>{evidence.relative_review_reduction_formula}</code>
      </p>

      {!evidence.recalls_are_equal && (
        <p className="error-text">
          두 후보의 테스트 Fraud Recall이 동일하지 않습니다 (Rule {ratio(rule.fraud_recall)} vs ML{" "}
          {ratio(ml.fraud_recall)}). 따라서 &quot;동일한 미탐률에서의 비교&quot;가 아니며, 자동화
          수치는 이 차이와 함께 읽어야 합니다.
        </p>
      )}

        <p className="muted">
          현재 서빙 중인 기본 Predictor: <code>{evidence.active_predictor_backend_default}</code> ·
          확률 캘리브레이션 적용: {evidence.calibration_applied ? "예" : "아니오"} ·{" "}
          {evidence.target_fraud_recall_basis}
        </p>
      </details>

        <div className="stamp-row">
          {stamped ? (
            <p className="stamp-done">✅ 도장 완료 — 불합격 성적표까지 확인하셨어요!</p>
          ) : (
            <button
              type="button"
              className="stamp-button"
              onClick={() => {
                setStamped(true);
                completeMission("try-numbers");
              }}
            >
              🧑‍⚖️ 성적표 확인 도장 찍기 — 미션 4 클리어
            </button>
          )}
        </div>
      </section>
    </>
  );
}
